import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiGithub, FiExternalLink, FiX, FiMaximize2 } from 'react-icons/fi';

const ProjectsSection = styled.section`
  padding: 100px 0;
  background: #0a192f;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(90deg, rgba(10, 25, 47, 0.9) 0%, rgba(10, 25, 47, 0.2) 50%, rgba(10, 25, 47, 0.9) 100%),
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(100, 255, 218, 0.03) 2px,
        rgba(100, 255, 218, 0.03) 4px
      );
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 clamp(10px, 5vw, 40px);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 400px), 1fr));
  gap: clamp(20px, 3vw, 40px);
  margin-top: 50px;
  width: 100%;
  max-width: 1300px;
  justify-content: center;

  @media (min-width: 1200px) {
    grid-template-columns: repeat(2, minmax(400px, 600px));
  }

  @media (max-width: 768px) {
    grid-template-columns: minmax(280px, 500px);
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(17, 34, 64, 0.95);
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid rgba(100, 255, 218, 0.1);
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  position: relative;
  aspect-ratio: 16/9;

  &:hover {
    border-color: rgba(100, 255, 218, 0.3);
    transform: translateY(-5px);
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const ProjectOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 25, 47, 0.9);
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${ProjectCard}:hover & {
    opacity: 1;
  }
`;

const ProjectTitle = styled.h3`
  color: #64ffda;
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  margin: 0;
  background: linear-gradient(to right, #64ffda, #00bcd4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ProjectBrief = styled.p`
  color: #8892b0;
  font-size: clamp(0.9rem, 2vw, 1rem);
  margin: 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
`;

const TechTag = styled.span`
  background: rgba(100, 255, 218, 0.1);
  color: #64ffda;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
`;

const ProjectActions = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #64ffda;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 5px;
  transition: all 0.3s ease;

  &:hover {
    color: #ccd6f6;
    transform: translateY(-2px);
  }
`;

const DetailModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 25, 47, 0.95);
  z-index: 1000;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
`;

const ModalContent = styled(motion.div)`
  background: #112240;
  border-radius: 15px;
  max-width: 900px;
  width: 100%;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  padding: 20px;
  margin: 20px;
  border: 1px solid rgba(100, 255, 218, 0.1);

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  grid-column: 1 / -1;

  @media (min-width: 768px) {
    grid-column: 1;
    grid-row: 1 / span 2;
  }
`;

const ModalInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ProjectDescription = styled.p`
  color: #8892b0;
  font-size: 1rem;
  line-height: 1.6;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 2;
`;

const projectData = [
  {
    id: 1,
    title: "ft_transcendence",
    description: "Un jeu de Pong multijoueur en temps réel avec chat intégré, système d'authentification et classement des joueurs. Développé dans le cadre du projet final de l'école 42.",
    image: "/images/ft_trancendence.png",
    tech: ["NestJS", "React", "PostgreSQL", "WebSocket", "OAuth2", "Docker"],
    github: "https://github.com/Arcadiastyx/ft_transcendence",
    live: ""
  },
  {
    id: 2,
    title: "CV Web Interactif",
    description: "CV web moderne et interactif développé avec Vite, React et TypeScript. Interface utilisateur élégante et responsive avec un terminal de commandes intégré permettant d'explorer mon parcours de manière interactive. Une approche unique pour présenter mes compétences et expériences professionnelles.",
    image: "/images/CV_web.png",
    tech: ["React", "TypeScript", "Vite", "Styled Components"],
    github: "https://github.com/Arcadiastyx/CV-Isaac",
    live: "https://isaac-naranjo-cv.netlify.app/"
  },
  {
    id: 3,
    title: "Portofolio Web Interactif",
    description: "Découvrez l'excellence du développement moderne avec ce template TypeScript optimisé. Combinant la puissance de Vite, la flexibilité de Tailwind CSS, et la rigueur d'ESLint, ce projet représente ma vision d'une architecture frontend robuste et évolutive. Une base solide pour vos futures applications web, alliant performance, maintenabilité et expérience développeur optimale.",
    image: "/images/CV_web1.png",
    tech: ["TypeScript", "Vite", "Tailwind CSS", "ESLint"],
    github: "https://github.com/Arcadiastyx/CV-2",
    live: "https://cv-example-template.netlify.app/"
  },
  {
    id: 4,
    title: "Site Web : Arcade Hêtre",
    description: "Le site Arcade Hêtre est un projet immersif et interactif conçu pour offrir une expérience utilisateur captivante grâce à des technologies modernes. Développé avec Three.js, il intègre des graphismes 3D interactifs et des animations fluides pour plonger les visiteurs dans un univers visuel unique.",
    image: "/images/arcade-hetre.png",
    tech: ["Three.js", "React", "WebGL", "GSAP", "Blender"],
    github: "https://github.com/Arcadiastyx/arcade-hetre",
    live: "https://arcade-hetre.netlify.app/"
  },
  {
    id: 5,
    title: "Portfolio Personnel",
    description: "Un portfolio moderne et opérationnel développé avec React, Node.js et MongoDB. Utilise des animations fluides et un design responsive. Intègre un système de gestion des formulaires de contact via emailService.js pour une communication directe et sécurisée avec les visiteurs. L'architecture complète permet une expérience utilisateur fluide et une maintenance facilitée.",
    image: "/images/portfolio_personnel.png",
    tech: ["React", "Node.js", "MongoDB", "Three.js", "Docker", "Railway"],
    github: "https://github.com/Arcadiastyx/portfolio-backend",
    live: "#"
  }
];

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <ProjectsSection id="projects">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 style={{ 
            fontSize: 'clamp(2rem, 5vw, 2.5rem)', 
            color: '#ccd6f6',
            textAlign: 'center',
            marginBottom: '50px'
          }}>
            Mes Projets
          </h2>
        </motion.div>

        <ProjectsGrid>
          {projectData.map((project, index) => (
            <ProjectCard
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setSelectedProject(project)}
            >
              <ProjectImage src={project.image} alt={project.title} />
              <ProjectOverlay
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
              >
                <div>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectBrief>{project.description}</ProjectBrief>
                </div>
                <div>
                  <TechStack>
                    {project.tech.slice(0, 3).map((tech, index) => (
                      <TechTag key={index}>{tech}</TechTag>
                    ))}
                    {project.tech.length > 3 && (
                      <TechTag>+{project.tech.length - 3}</TechTag>
                    )}
                  </TechStack>
                  <ProjectActions>
                    {project.github && (
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(project.github, '_blank');
                        }}
                      >
                        <FiGithub />
                      </IconButton>
                    )}
                    {project.live && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(project.live, '_blank');
                        }}
                      >
                        <FiExternalLink />
                      </IconButton>
                    )}
                    <IconButton>
                      <FiMaximize2 />
                    </IconButton>
                  </ProjectActions>
                </div>
              </ProjectOverlay>
            </ProjectCard>
          ))}
        </ProjectsGrid>

        <AnimatePresence>
          {selectedProject && (
            <DetailModal
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
            >
              <ModalContent
                onClick={e => e.stopPropagation()}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                <CloseButton onClick={() => setSelectedProject(null)}>
                  <FiX />
                </CloseButton>
                <ModalImage src={selectedProject.image} alt={selectedProject.title} />
                <ModalInfo>
                  <div>
                    <ProjectTitle>{selectedProject.title}</ProjectTitle>
                    <ProjectDescription>{selectedProject.description}</ProjectDescription>
                  </div>
                  <TechStack>
                    {selectedProject.tech.map((tech, index) => (
                      <TechTag key={index}>{tech}</TechTag>
                    ))}
                  </TechStack>
                  <ProjectActions>
                    {selectedProject.github && (
                      <IconButton onClick={() => window.open(selectedProject.github, '_blank')}>
                        <FiGithub />
                      </IconButton>
                    )}
                    {selectedProject.live && (
                      <IconButton onClick={() => window.open(selectedProject.live, '_blank')}>
                        <FiExternalLink />
                      </IconButton>
                    )}
                  </ProjectActions>
                </ModalInfo>
              </ModalContent>
            </DetailModal>
          )}
        </AnimatePresence>
      </Container>
    </ProjectsSection>
  );
};

export default Projects;
